import {
  CheckCircle,
  Error,
  ExpandMore,
  Refresh,
  Warning,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

import { ConfigurationValidationResult } from '../../services/configuration-api';

interface ConfigurationValidationProps {
  validationResults?: ConfigurationValidationResult;
  onRefresh: () => void;
}

export const ConfigurationValidation: React.FC<
  ConfigurationValidationProps
> = ({ validationResults, onRefresh }) => {
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'summary',
  ]);

  const handleSectionToggle = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  if (!validationResults) {
    return (
      <Box>
        <Typography variant='body1' color='text.secondary'>
          No validation results available. Click refresh to validate
          configuration.
        </Typography>
        <Button
          variant='outlined'
          startIcon={<Refresh />}
          onClick={onRefresh}
          sx={{ mt: 2 }}
        >
          Validate Configuration
        </Button>
      </Box>
    );
  }

  const getStatusIcon = (isValid: boolean, hasWarnings: boolean) => {
    if (!isValid) return <Error color='error' />;
    if (hasWarnings) return <Warning color='warning' />;
    return <CheckCircle color='success' />;
  };

  const getStatusColor = (isValid: boolean, hasWarnings: boolean) => {
    if (!isValid) return 'error';
    if (hasWarnings) return 'warning';
    return 'success';
  };

  const getSeverityIcon = (severity: 'error' | 'warning') => {
    return severity === 'error' ? (
      <Error color='error' />
    ) : (
      <Warning color='warning' />
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant='h6'>Configuration Validation Results</Typography>
        <Button variant='outlined' startIcon={<Refresh />} onClick={onRefresh}>
          Refresh
        </Button>
      </Box>

      {/* Summary */}
      <Accordion
        expanded={expandedSections.includes('summary')}
        onChange={() => handleSectionToggle('summary')}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {getStatusIcon(
              validationResults.isValid,
              validationResults.warnings.length > 0
            )}
            <Typography variant='h6'>Overall Status</Typography>
            <Chip
              label={validationResults.isValid ? 'Valid' : 'Invalid'}
              color={getStatusColor(
                validationResults.isValid,
                validationResults.warnings.length > 0
              )}
              size='small'
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Paper sx={{ p: 2, textAlign: 'center', minWidth: 120 }}>
              <Typography variant='h4' color='error'>
                {validationResults.errors.length}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Errors
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center', minWidth: 120 }}>
              <Typography variant='h4' color='warning.main'>
                {validationResults.warnings.length}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Warnings
              </Typography>
            </Paper>
            {validationResults.testResults && (
              <Paper sx={{ p: 2, textAlign: 'center', minWidth: 120 }}>
                <Typography variant='h4' color='primary'>
                  {validationResults.testResults.filter(r => r.passed).length}/
                  {validationResults.testResults.length}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Tests Passed
                </Typography>
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Errors */}
      {validationResults.errors.length > 0 && (
        <Accordion
          expanded={expandedSections.includes('errors')}
          onChange={() => handleSectionToggle('errors')}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Error color='error' />
              <Typography variant='h6'>
                Errors ({validationResults.errors.length})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity='error' sx={{ mb: 2 }}>
              These errors must be fixed before the configuration can be used.
            </Alert>
            <List>
              {validationResults.errors.map((error, index) => (
                <ListItem key={index} divider>
                  <ListItemIcon>{getSeverityIcon('error')}</ListItemIcon>
                  <ListItemText
                    primary={error.message}
                    secondary={
                      <Box>
                        <Typography variant='caption' display='block'>
                          Field: {error.field}
                        </Typography>
                        <Typography variant='caption' display='block'>
                          Code: {error.code}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Warnings */}
      {validationResults.warnings.length > 0 && (
        <Accordion
          expanded={expandedSections.includes('warnings')}
          onChange={() => handleSectionToggle('warnings')}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Warning color='warning' />
              <Typography variant='h6'>
                Warnings ({validationResults.warnings.length})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity='warning' sx={{ mb: 2 }}>
              These warnings indicate potential issues that should be reviewed.
            </Alert>
            <List>
              {validationResults.warnings.map((warning, index) => (
                <ListItem key={index} divider>
                  <ListItemIcon>{getSeverityIcon('warning')}</ListItemIcon>
                  <ListItemText
                    primary={warning.message}
                    secondary={
                      <Box>
                        <Typography variant='caption' display='block'>
                          Field: {warning.field}
                        </Typography>
                        <Typography variant='caption' display='block'>
                          Code: {warning.code}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Test Results */}
      {validationResults.testResults &&
        validationResults.testResults.length > 0 && (
          <Accordion
            expanded={expandedSections.includes('tests')}
            onChange={() => handleSectionToggle('tests')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle color='primary' />
                <Typography variant='h6'>
                  Formula Test Results ({validationResults.testResults.length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Alert
                severity={
                  validationResults.testResults.every(r => r.passed)
                    ? 'success'
                    : 'warning'
                }
                sx={{ mb: 2 }}
              >
                {validationResults.testResults.every(r => r.passed)
                  ? 'All formula tests passed successfully.'
                  : 'Some formula tests failed. Review the results below.'}
              </Alert>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Test Case</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align='right'>Expected</TableCell>
                      <TableCell align='right'>Actual</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationResults.testResults.map((result, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: result.passed
                            ? 'success.light'
                            : 'error.light',
                          opacity: result.passed ? 1 : 0.8,
                        }}
                      >
                        <TableCell>{result.testCase}</TableCell>
                        <TableCell>
                          {result.passed ? (
                            <Chip
                              icon={<CheckCircle />}
                              label='Passed'
                              color='success'
                              size='small'
                            />
                          ) : (
                            <Chip
                              icon={<Error />}
                              label='Failed'
                              color='error'
                              size='small'
                            />
                          )}
                        </TableCell>
                        <TableCell align='right'>
                          {result.expected.toLocaleString()}
                        </TableCell>
                        <TableCell align='right'>
                          {result.actual.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {result.error ? (
                            <Typography variant='caption' color='error'>
                              {result.error}
                            </Typography>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}

      {/* Success Message */}
      {validationResults.isValid &&
        validationResults.errors.length === 0 &&
        validationResults.warnings.length === 0 && (
          <Alert severity='success' sx={{ mt: 2 }}>
            <Typography variant='h6' gutterBottom>
              Configuration is Valid! 🎉
            </Typography>
            <Typography variant='body2'>
              Your metrics configuration has been validated successfully. All
              metric definitions, work item mappings, and formulas are properly
              configured and ready to use.
            </Typography>
          </Alert>
        )}

      {/* Recommendations */}
      {(validationResults.warnings.length > 0 ||
        !validationResults.isValid) && (
        <Alert severity='info' sx={{ mt: 2 }}>
          <Typography variant='h6' gutterBottom>
            Recommendations
          </Typography>
          <Typography variant='body2' component='div'>
            <ul>
              {!validationResults.isValid && (
                <li>
                  Fix all errors before using the configuration in production
                </li>
              )}
              {validationResults.warnings.length > 0 && (
                <li>
                  Review and address warnings to improve configuration quality
                </li>
              )}
              {validationResults.testResults &&
                validationResults.testResults.some(r => !r.passed) && (
                  <li>
                    Update formula test cases or fix formula logic for failing
                    tests
                  </li>
                )}
              <li>Regularly validate configuration after making changes</li>
              <li>
                Test formulas with realistic data before deploying to production
              </li>
            </ul>
          </Typography>
        </Alert>
      )}
    </Box>
  );
};
